import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";
import type { PersistField } from "../../../hooks/use-auto-save-field";
import { useAutoSaveField } from "../../../hooks/use-auto-save-field";

const GET_SALON_CONFIGS = gql(`
  query GetSalonConfigurations($salonId: ID!, $category: String) {
    getSalonConfigurations(salonId: $salonId, filters: { category: $category }) {
      configurations {
        key
        value
        dataType
        category
        createdAt
        updatedAt
      }
    }
  }
`);

const UPDATE_SALON_CONFIGURATIONS = gql(`
  mutation UpdateSalonConfigurations($salonId: ID!, $input: UpdateSalonConfigurationsInput!) {
    updateSalonConfigurations(salonId: $salonId, input: $input) {
      key
      value
      dataType
      category
      updatedAt
    }
  }
`);

export interface RewardsConfig {
  pointsPerDollar: number;
  pointsPerVisit: number;
  enabled: boolean;
  minimumSpendThreshold: number; // in cents
  loyaltyProgramEnabled: boolean; // salon-level master toggle
}
export function useRewardsConfig() {
  const { currentSalon } = useSalonStore();
  const selectedSalonId = currentSalon?.id;
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<Error | null>(null);

  // Get both loyalty and salon configurations
  const {
    data: loyaltyData,
    loading: loyaltyLoading,
    error: loyaltyError,
  } = useQuery(GET_SALON_CONFIGS, {
    variables: {
      salonId: selectedSalonId ?? "",
      category: "loyalty",
    },
    skip: !selectedSalonId,
  });

  const {
    data: salonData,
    loading: salonLoading,
    error: salonError,
  } = useQuery(GET_SALON_CONFIGS, {
    variables: {
      salonId: selectedSalonId ?? "",
      category: "salon",
    },
    skip: !selectedSalonId,
  });

  const [updateConfigsMutation] = useMutation(UPDATE_SALON_CONFIGURATIONS);

  const isLoading = loyaltyLoading || salonLoading;
  const error = loyaltyError || salonError;

  // Parse configurations into typed object
  const defaultValues: RewardsConfig = useMemo(() => {
    const loyaltyConfigurations = loyaltyData?.getSalonConfigurations?.configurations || [];
    const salonConfigurations = salonData?.getSalonConfigurations?.configurations || [];
    return {
      pointsPerDollar: parseConfigValue(
        loyaltyConfigurations,
        "loyalty.points_per_dollar",
        "int",
        1
      ),
      pointsPerVisit: parseConfigValue(loyaltyConfigurations, "loyalty.points_per_visit", "int", 0),
      enabled: parseConfigValue(loyaltyConfigurations, "loyalty.enabled", "boolean", true),
      minimumSpendThreshold: parseConfigValue(
        loyaltyConfigurations,
        "loyalty.minimum_spend_threshold",
        "int",
        0
      ),
      loyaltyProgramEnabled: parseConfigValue(
        salonConfigurations,
        "salon.loyalty_program_enabled",
        "boolean",
        true
      ),
    };
  }, [loyaltyData, salonData]);

  // Auto-sync with Apollo cache (initial load + refetch after save) while
  // preserving user edits in flight.
  const form = useForm<RewardsConfig>({
    values: defaultValues,
    resetOptions: { keepDirtyValues: true },
    mode: "onChange",
  });

  const persistField: PersistField<RewardsConfig> = useCallback(
    async (name, value) => {
      if (!selectedSalonId) {
        throw new Error("No salon selected");
      }
      return updateConfigsMutation({
        variables: {
          salonId: selectedSalonId,
          input: { configurations: [toRewardsConfiguration(name, value)] },
        },
        refetchQueries: [GET_SALON_CONFIGS],
      });
    },
    [selectedSalonId, updateConfigsMutation]
  );
  const { saveField, savingFields } = useAutoSaveField(form, persistField);

  // Save function
  const save = useCallback(async () => {
    if (!form.formState.isDirty || !selectedSalonId) {
      toast.info("No changes to save");
      return;
    }

    setSaving(true);
    setMutationError(null);

    try {
      const formData = form.getValues();

      const configurations = (Object.keys(rewardsFields) as (keyof RewardsConfig)[]).map(
        (name) => ({
          category: rewardsFields[name].category,
          configuration: toRewardsConfiguration(name, formData[name]),
        })
      );
      const loyaltyConfigs = configurations
        .filter(({ category }) => category === "loyalty")
        .map(({ configuration }) => configuration);
      const salonConfigs = configurations
        .filter(({ category }) => category === "salon")
        .map(({ configuration }) => configuration);

      // Update both categories
      await Promise.all([
        updateConfigsMutation({
          variables: {
            salonId: selectedSalonId,
            input: { configurations: loyaltyConfigs },
          },
        }),
        updateConfigsMutation({
          variables: {
            salonId: selectedSalonId,
            input: { configurations: salonConfigs },
          },
          refetchQueries: [GET_SALON_CONFIGS],
        }),
      ]);

      // Mark form as pristine after successful save
      form.reset(form.getValues(), { keepValues: true });
      toast.success("Rewards configuration saved successfully");
    } catch (error) {
      console.error("Rewards configuration save error:", error);
      const mutationErr = error instanceof Error ? error : new Error("Save failed");
      setMutationError(mutationErr);
      toast.error(`Save failed: ${mutationErr.message}`);
      throw error;
    } finally {
      setSaving(false);
    }
  }, [form, selectedSalonId, updateConfigsMutation]);

  // Discard function
  const discard = useCallback(() => {
    form.reset(defaultValues);
    setMutationError(null);
    toast.info("Changes discarded");
  }, [form, defaultValues]);

  const combinedError = error || mutationError;

  return {
    form,
    config: form.watch(), // Return current form values
    isLoading,
    saving,
    error: combinedError,
    save,
    discard,
    saveField,
    savingFields,
  };
}

function toRewardsConfiguration<K extends keyof RewardsConfig>(name: K, value: RewardsConfig[K]) {
  const { category: _, ...configuration } = rewardsFields[name];
  return { ...configuration, value: String(value) };
}

const rewardsFields: Record<
  keyof RewardsConfig,
  { key: string; dataType: string; category: "loyalty" | "salon" }
> = {
  pointsPerDollar: { key: "loyalty.points_per_dollar", dataType: "int", category: "loyalty" },
  pointsPerVisit: { key: "loyalty.points_per_visit", dataType: "int", category: "loyalty" },
  enabled: { key: "loyalty.enabled", dataType: "boolean", category: "loyalty" },
  minimumSpendThreshold: {
    key: "loyalty.minimum_spend_threshold",
    dataType: "int",
    category: "loyalty",
  },
  loyaltyProgramEnabled: {
    key: "salon.loyalty_program_enabled",
    dataType: "boolean",
    category: "salon",
  },
};

// Helper functions
function parseConfigValue<T>(
  configurations: any[],
  key: string,
  dataType: string,
  defaultValue: T
): T {
  const config = configurations.find((c) => c.key === key);
  if (!config) return defaultValue;

  switch (dataType) {
    case "boolean":
      return (config.value.toLowerCase() === "true") as T;
    case "int":
      return Number.parseInt(config.value, 10) as T;
    default:
      return config.value as T;
  }
}
